// client/src/v2/components/drawer/ExamDetailsPanel.jsx

import React, { useMemo, useState } from "react";
import {
  isExamSectionEnabled,
  normalizeExamStages,
  flattenKeyDates,
  resolveKeyDatesForPanel,
  formatAdmissionTestYesNo,
  splitAssessmentForStages,
} from "../../utils/examDetailsUtils";
import KeyDateFlow from "./stats/KeyDateFlow";
import "./stats/drawerStats.css";

function externalHref(url) {
  if (!url || !String(url).trim()) return null;
  const u = String(url).trim();
  return u.startsWith("http") ? u : `https://${u}`;
}

function ExamInfoLink({ url }) {
  const href = externalHref(url);
  if (!href) return null;
  return (
    <div>
      <span>Exam info</span>
      <strong>
        <a className="v2-drawer-link" href={href} target="_blank" rel="noreferrer">
          School exam information →
        </a>
      </strong>
    </div>
  );
}

/**
 * 11+ exam section: year toggles + blocks gated by admissions_policies.show_* flags.
 */
export default function ExamDetailsPanel({ policies, loading, error }) {
  const list = useMemo(() => (Array.isArray(policies) ? policies : []), [policies]);

  const years = useMemo(() => {
    const ys = list.map((p) => p.entry_year).filter((y) => y != null);
    return Array.from(new Set(ys)).sort((a, b) => (b || 0) - (a || 0));
  }, [list]);

  const [selectedYear, setSelectedYear] = useState(null);

  const effectiveYear = useMemo(() => {
    if (selectedYear != null && years.includes(selectedYear)) return selectedYear;
    return years[0] ?? null;
  }, [years, selectedYear]);

  const policy = useMemo(() => {
    if (effectiveYear == null) return null;
    return list.find((p) => p.entry_year === effectiveYear) ?? null;
  }, [list, effectiveYear]);

  const stages = useMemo(() => normalizeExamStages(policy?.exam_stages), [policy?.exam_stages]);
  const dateRows = useMemo(() => flattenKeyDates(resolveKeyDatesForPanel(policy)), [policy]);

  const assessmentType = policy?.exam_assessment_type;
  const assessmentTrimmed = typeof assessmentType === "string" ? assessmentType.trim() : "";
  const assessmentPerStage = useMemo(
    () => splitAssessmentForStages(assessmentTrimmed, stages.length),
    [assessmentTrimmed, stages.length]
  );

  /** Same authority for every stage → show once above stages (Tiffin-style). */
  const sharedTestAuthority = useMemo(() => {
    if (!policy || !stages.length) return null;
    const resolved = stages.map(
      (s) => s.test_authority?.trim() || policy.test_authority?.trim() || "—"
    );
    const first = resolved[0];
    if (!first || first === "—") return null;
    return resolved.every((a) => a === first) ? first : null;
  }, [policy, stages]);

  if (loading) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">Loading exam details…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted" style={{ color: "#c00" }}>
          {error}
        </div>
      </div>
    );
  }

  if (!list.length) {
    return (
      <div className="v2-catchment-panel">
        <div className="v2-muted">No admissions policy rows for this school yet.</div>
      </div>
    );
  }

  const showDetails = policy && isExamSectionEnabled(policy.show_exam_details);
  const showDates = policy && isExamSectionEnabled(policy.show_exam_dates);
  const showNotes = policy && isExamSectionEnabled(policy.show_exam_notes);
  const notesText = policy ? String(policy.notes ?? "").trim() : "";

  const hasAnyBlock = showDetails || showDates || showNotes;

  return (
    <div className="v2-catchment-panel">
      {years.length > 0 ? (
        <div className="v2-catchment-year-toggles">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              className={`v2-catchment-year-btn ${y === effectiveYear ? "v2-catchment-year-btn--active" : ""}`}
              onClick={() => setSelectedYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      ) : null}

      <div className="v2-catchment-content v2-exam-stack">
        {!hasAnyBlock ? (
          <p className="v2-muted">Exam section flags are off for this year (enable in DB when ready).</p>
        ) : null}

        {showDetails ? (
          <section className="ds-section">
            <h4 className="ds-section-title">Exam details</h4>
            <div className="ds-contact">
              <div>
                <span>Entrance test</span>
                <strong>{formatAdmissionTestYesNo(policy.admission_test)}</strong>
              </div>
              {sharedTestAuthority || !stages.length ? (
                <div>
                  <span>Conducted by</span>
                  <strong>{sharedTestAuthority || policy.test_authority?.trim() || "—"}</strong>
                </div>
              ) : null}
              {sharedTestAuthority || !stages.length ? <ExamInfoLink url={policy.exam_info_url} /> : null}
              {!stages.length ? (
                <div>
                  <span>Assessment</span>
                  <strong>{assessmentTrimmed || "—"}</strong>
                </div>
              ) : null}
            </div>
            {stages.length > 0 ? (
              <ol className="ds-flow" aria-label="Exam stages">
                {stages.map((s, idx) => {
                  const authority = s.test_authority?.trim() || policy.test_authority?.trim() || "—";
                  const assessmentLine =
                    s.assessment_type?.trim() ||
                    (assessmentPerStage && assessmentPerStage[idx] != null && assessmentPerStage[idx] !== ""
                      ? assessmentPerStage[idx]
                      : assessmentTrimmed || "—");
                  const subjects = Array.isArray(s.subjectEntries) ? s.subjectEntries : [];
                  return (
                    <li className="ds-flow-step" key={`exam-stage-${idx}-${s.stage}`}>
                      <span className="ds-flow-node" aria-hidden="true">{s.stage || idx + 1}</span>
                      <div className="ds-flow-card">
                        <div className="ds-flow-label">Stage {s.stage}</div>
                        {subjects.length ? (
                          <div className="ds-pills">
                            {subjects.map((sub, subjectIndex) => (
                              <span className="ds-pill" key={`${sub.name}-${subjectIndex}`}>
                                <em>{sub.name}</em>
                                {sub.note ? ` (${sub.note})` : ""}
                                {sub.url ? (
                                  <>
                                    {" "}
                                    <a className="v2-drawer-link" href={externalHref(sub.url)} target="_blank" rel="noreferrer">
                                      {sub.linkLabel || "Mocks"}
                                    </a>
                                  </>
                                ) : null}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="ds-flow-value">—</div>
                        )}
                        {!sharedTestAuthority ? (
                          <div className="ds-flow-value">Test conducted by {authority}</div>
                        ) : null}
                        <div className="ds-flow-value">{assessmentLine}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <p className="v2-muted">No stages in data yet.</p>
            )}
          </section>
        ) : null}

        {showDates ? (
          <section className="ds-section">
            <h4 className="ds-section-title">Key dates</h4>
            {dateRows.length ? <KeyDateFlow rows={dateRows} /> : <p className="v2-muted">No dates in data yet.</p>}
          </section>
        ) : null}

        {showNotes ? (
          <section className="ds-section">
            <h4 className="ds-section-title">Notes</h4>
            {notesText ? (
              <p className="ds-note">{notesText}</p>
            ) : (
              <p className="v2-muted">No notes in data yet.</p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
